import {
  AlertTriangle, CheckCircle2, ChevronLeft, Copy, Eye, EyeOff, Info, Loader2, ShieldAlert, X,
} from 'lucide-react';
import { useI18n } from '@/i18n/context';
import {
  createContext, useContext, useEffect, useId, useMemo, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type KeyboardEvent as ReactKeyboardEvent, type ReactNode,
  type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from './Breadcrumbs';
import { useAutoCrumbs, type Crumb } from '@/lib/nav-trail';
import { StateArt, type StateArtKind } from './Brand';
import { DateParts } from './DateParts';
import { DatePickerField } from './calendar/DatePickerField';
import { currencyLabel, type Tone } from '@/lib/labels';
import { NativeDisabledButtons } from './native-disabled';

/* ───────────────────────────── الأزرار ───────────────────────────── */
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'md' | 'sm';
  block?: boolean;
  loading?: boolean;
  icon?: ReactNode;
  /** سبب التعطيل — يُعرَض تحت الزر بدل إخفائه (قاعدة إلزامية من القسم 08) */
  disabledReason?: string;
  /** بابُ ما ينقص (البند 5): مع سبب التعطيل رابطُ «انتقل إلى …» — لا زرَّ معطّلاً بلا مخرج */
  nextStep?: { label: string; to: string } | null;
  /**
   * ما يحدث حين يُضغط — سطرٌ تحت الزرّ في الغلاف نفسِه (27 سبتمبر 2026): «يُرسل للمراجعة ولا يُنشر
   * قبل الاعتماد». وسببُ التعطيل يحلّ محلّه حين يظهر — كما يحلّ خطأُ الحقل محلَّ تلميحه.
   */
  hint?: string;
}

/**
 * الزرّ الذي يُمرَّر له `disabledReason` — **ولو بقيمة `undefined`** — يُبنى دائماً داخل غلافٍ واحد
 * (`.btn-wrap`) يحمل سطرَ التوضيح مخفياً أو ظاهراً (جولة الجوال، 26 سبتمبر 2026). قبلها كان الغلافُ
 * يظهر مع التعطيل ويختفي مع التفعيل، فيعيد React تركيبَ الزرّ في الشجرة: يضيع التركيزُ من لوحة
 * المفاتيح، ويتوسّط الغلافُ الأطولُ عمودياً في `.row` فيرتفع الزرّ عن جاره.
 *
 * ومعطَّلُ هذا الزرّ `aria-disabled` لا `disabled`: الزرُّ المعطَّل بالسمة يخرج من ترتيب Tab ويفقد
 * التركيزَ لحظةَ تعطيله، والسببُ المكتوب تحته لا يصل قارئَ الشاشة إلا إن أمكن الوقوفُ عليه. والنقرُ
 * عليه (وإرسالُ النموذج الضمنيُّ بـEnter الذي يمرّ بنقرةٍ على زرّ الإرسال) يُلغى هنا.
 */
export function Button(props: ButtonProps) {
  const {
    variant = 'secondary', size = 'md', block, loading, icon, disabledReason, hint, nextStep,
    disabled, children, className = '', type = 'button', onClick, ...rest
  } = props;
  const reasonId = useId();
  const native = useContext(NativeDisabledButtons);
  const slot = Object.prototype.hasOwnProperty.call(props, 'disabledReason') || !!hint;
  const isDisabled = !!(disabled || loading);
  const showReason = !!disabledReason && isDisabled;
  const showHint = !!hint && !showReason;
  const describedBy = [rest['aria-describedby'], showReason || showHint ? reasonId : null].filter(Boolean).join(' ') || undefined;
  const cls = `btn btn--${variant}${size === 'sm' ? ' btn--sm' : ''}${block ? ' btn--block' : ''} ${className}`.trim();
  const inner = (
    <>
      {loading ? <Loader2 size={15} className="btn__spin" aria-hidden style={{ animation: 'spin .7s linear infinite', border: 0 }} /> : icon}
      {children}
    </>
  );
  if (!slot) {
    return (
      <button type={type} className={cls} disabled={isDisabled} aria-busy={loading || undefined} onClick={onClick} {...rest}>
        {inner}
      </button>
    );
  }
  return (
    <span className={`btn-wrap${block ? ' btn-wrap--block' : ''}`}>
      <button
        type={type}
        className={cls}
        {...rest}
        disabled={native ? isDisabled : undefined}
        aria-disabled={!native && isDisabled ? true : undefined}
        aria-busy={loading || undefined}
        aria-describedby={describedBy}
        onClick={(e) => {
          if (isDisabled) { e.preventDefault(); e.stopPropagation(); return; }
          onClick?.(e);
        }}
      >
        {inner}
      </button>
      <span className={`btn-reason${showHint ? ' btn-hint' : ''}`} id={reasonId} hidden={!showReason && !showHint}>
        {showReason ? disabledReason : showHint ? hint : null}
        {showReason && nextStep && <> <Link to={nextStep.to} className="btn-reason__step">{`انتقل إلى: ${nextStep.label}`}</Link></>}
      </span>
    </span>
  );
}

/* ───────────────────────────── الحقول ───────────────────────────── */
interface FieldShellProps {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string | null;
  children: (props: { id: string; 'aria-invalid': boolean; 'aria-describedby': string | undefined }) => ReactNode;
  className?: string;
}

export function Field({ label, required, hint, error, children, className = '' }: FieldShellProps) {
  const id = useId();
  const describedBy = error ? `${id}-err` : hint ? `${id}-hint` : undefined;
  return (
    <div className={`field ${className}`.trim()}>
      <label className="field__label" htmlFor={id}>
        {label}
        {required && <span className="field__req" aria-hidden>*</span>}
        {required && <span className="sr-only"> (حقل إلزامي)</span>}
      </label>
      {children({ id, 'aria-invalid': !!error, 'aria-describedby': describedBy })}
      {hint && !error && <span className="field__hint" id={`${id}-hint`}>{hint}</span>}
      {error && (
        <span className="field__error" id={`${id}-err`} role="alert">
          <AlertTriangle size={13} aria-hidden />{error}
        </span>
      )}
    </div>
  );
}

type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string; hint?: string; error?: string | null; ltr?: boolean; wrapClassName?: string;
};
export function TextInput({ label, hint, error, ltr, required, wrapClassName, className = '', type, ...rest }: TextInputProps) {
  if (type === 'password') return <PasswordInput label={label} hint={hint} error={error} ltr={ltr} required={required} wrapClassName={wrapClassName} className={className} {...rest} />;
  // لا حقلَ تاريخٍ من المتصفّح (نصُّه يُقلب في كروم بلغة جهازٍ عربية): التاريخُ والموعدُ بمنتقي التقويم
  // (11 أكتوبر 2026 — مربّعاتُ أيامٍ ونقاطُ المحجوز)، والشهرُ وحده بقوائم DateParts.
  if (type === 'date' || type === 'datetime-local' || type === 'month') {
    const { value, onChange, min, max, disabled, name } = rest;
    const common = { value: value == null ? '' : String(value), onChange, min: min == null ? undefined : String(min), max: max == null ? undefined : String(max), disabled, name, required, past: /الميلاد/.test(label) };
    return (
      <Field label={label} required={required} hint={hint} error={error} className={wrapClassName}>
        {(a) => type === 'month'
          ? <DateParts {...a} kind="month" {...common} />
          : <DatePickerField {...a} kind={type as 'date' | 'datetime-local'} {...common} />}
      </Field>
    );
  }
  return (
    <Field label={label} required={required} hint={hint} error={error} className={wrapClassName}>
      {(a) => <input {...a} {...rest} type={type} required={required} className={`input${ltr ? ' input--ltr' : ''} ${className}`.trim()} />}
    </Field>
  );
}

/**
 * كلمة المرور بزرّ إظهار/إخفاء: كل حقل `type="password"` في المنصة يمرّ من هنا
 * (13 سبتمبر 2026). الزرّ لا يدخل تسلسل Tab قبل الحقل، واسمه لقارئ الشاشة يتغيّر بحاله.
 */
function PasswordInput({ label, hint, error, ltr, required, wrapClassName, className = '', onKeyDown, onKeyUp, onBlur, ...rest }: Omit<TextInputProps, 'type'>) {
  const { t } = useI18n();
  const [shown, setShown] = useState(false);
  // Caps Lock (26 سبتمبر 2026): «بيانات غير صحيحة» بلا سببٍ ظاهر كان غالباً حرفاً كبيراً — يُقال قبل الرفض
  const [caps, setCaps] = useState(false);
  const readCaps = (e: ReactKeyboardEvent<HTMLInputElement>) => setCaps(typeof e.getModifierState === 'function' && e.getModifierState('CapsLock'));
  return (
    <Field label={label} required={required} hint={caps ? undefined : hint} error={error} className={wrapClassName}>
      {(a) => (<>
        <div className={`input-wrap${ltr ? ' input-wrap--ltr' : ''}`}>
          <input {...a} {...rest} type={shown ? 'text' : 'password'} required={required} className={`input input--with-btn${ltr ? ' input--ltr' : ''} ${className}`.trim()}
            onKeyDown={(e) => { readCaps(e); onKeyDown?.(e); }} onKeyUp={(e) => { readCaps(e); onKeyUp?.(e); }} onBlur={(e) => { setCaps(false); onBlur?.(e); }} />
          <button
            type="button" className="input-wrap__btn" onClick={() => setShown((v) => !v)}
            aria-label={t(shown ? 'password.hide' : 'password.show')} title={t(shown ? 'password.hide' : 'password.show')} aria-pressed={shown}
          >
            {shown ? <EyeOff size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
          </button>
        </div>
        {caps && !error && <span className="caps-hint" role="status"><AlertTriangle size={13} aria-hidden /> {t('password.capsLock')}</span>}
      </>)}
    </Field>
  );
}

type SelectFieldProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label: string; hint?: string; error?: string | null; wrapClassName?: string;
};
export function SelectField({ label, hint, error, required, children, wrapClassName, ...rest }: SelectFieldProps) {
  return (
    <Field label={label} required={required} hint={hint} error={error} className={wrapClassName}>
      {(a) => <select {...a} {...rest} required={required} className="select">{children}</select>}
    </Field>
  );
}

type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string; hint?: string; error?: string | null; wrapClassName?: string;
};
export function TextArea({ label, hint, error, required, wrapClassName, className = '', ...rest }: TextAreaProps) {
  return (
    <Field label={label} required={required} hint={hint} error={error} className={wrapClassName}>
      {(a) => <textarea {...a} {...rest} required={required} className={`textarea ${className}`.trim()} />}
    </Field>
  );
}

/* ───────────────────────────── الشارات ───────────────────────────── */
const toneIcon: Record<Tone, ReactNode> = {
  neutral: <span aria-hidden style={{ inlineSize: 6, blockSize: 6, borderRadius: 99, background: 'currentColor', display: 'inline-block' }} />,
  brand: <span aria-hidden style={{ inlineSize: 6, blockSize: 6, borderRadius: 99, background: 'currentColor', display: 'inline-block' }} />,
  success: <CheckCircle2 aria-hidden />,
  warning: <AlertTriangle aria-hidden />,
  danger: <ShieldAlert aria-hidden />,
};
/** شارة حالة = لون + أيقونة + نص معاً — لا يُكتفى بأي منها وحده */
export function Badge({ tone = 'neutral', long, children }: { tone?: Tone; long?: boolean; children: ReactNode }) {
  return <span className={`badge badge--${tone}${long ? ' badge--long' : ''}`}>{toneIcon[tone]}{children}</span>;
}

/* ───────────────────────────── التنبيهات ───────────────────────────── */
const noteIcon = { info: Info, success: CheckCircle2, warning: AlertTriangle, danger: ShieldAlert } as const;
export function Note({ kind = 'info', title, children }: { kind?: keyof typeof noteIcon; title?: string; children: ReactNode }) {
  const Icon = noteIcon[kind];
  return (
    <div className={`note note--${kind}`} role={kind === 'danger' ? 'alert' : undefined}>
      <Icon aria-hidden />
      <div>{title && <strong className="note__title">{title}</strong>}{children}</div>
    </div>
  );
}

/* ───────────────────────────── البطاقات ───────────────────────────── */
export function Card({ title, actions, children, soft, tight }: {
  title?: ReactNode; actions?: ReactNode; children: ReactNode; soft?: boolean; tight?: boolean;
}) {
  return (
    <section className={`card${soft ? ' card--soft' : ''}`}>
      {(title || actions) && (
        <header className="card__head">
          {title && <h2 className="card__title grow">{title}</h2>}
          {actions}
        </header>
      )}
      <div className={`card__body${tight ? ' card__body--tight' : ''}`}>{children}</div>
    </section>
  );
}

export function Kpi({ label, value, foot }: { label: string; value: ReactNode; foot?: ReactNode }) {
  return (
    <div className="kpi">
      <span className="kpi__label">{label}</span>
      <span className="kpi__value">{value}</span>
      {foot && <span className="kpi__foot">{foot}</span>}
    </div>
  );
}

/* ───────────────────────────── الحالة الفارغة ───────────────────────────── */
/** لا قائمة فارغة صامتة: رسمٌ يمثّل الحالة نفسها (لا رمزٌ موحّد) + جملة إرشادية + زر فعل حين يوجد */
export function EmptyState({ title, text, action, art = 'empty' }: { title: string; text: string; action?: ReactNode; art?: StateArtKind }) {
  return (
    <div className="empty">
      <StateArt kind={art} className="empty__art" />
      <p className="empty__title">{title}</p>
      <p className="empty__text">{text}</p>
      {action}
    </div>
  );
}

export function Skeleton({ height = 16, width = '100%' }: { height?: number; width?: number | string }) {
  return <span className="skeleton" style={{ display: 'block', blockSize: height, inlineSize: width }} aria-hidden />;
}

export function LoadingBlock({ label = 'جارٍ التحميل…' }: { label?: string }) {
  return (
    <div className="stack gap-3" style={{ padding: 'var(--s-6)' }} role="status" aria-live="polite">
      <span className="sr-only">{label}</span>
      <Skeleton height={20} width="42%" />
      <Skeleton height={14} />
      <Skeleton height={14} width="78%" />
      <Skeleton height={14} width="60%" />
    </div>
  );
}

/* ───────────────────────────── النوافذ ───────────────────────────── */
export function Modal({ title, description, onClose, children, footer, wide, dismissible = true }: {
  title: string; description?: string; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean;
  /** `false`: نافذةٌ إلزامية — لا زرَّ إغلاق ولا Escape ولا نقرةَ خلفية؛ تُغلق بزرّها في التذييل وحده (إشعارُ التحديث 0087) */
  dismissible?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // `onClose` يُمرَّر دالةً مضمّنةً جديدةً في كل تصيير عند 130+ موضع استدعاء. لو كان
  // اعتماداً للتأثير أدناه لأُعيد تشغيلُه مع كل ضغطة مفتاح داخل النافذة (الكتابة
  // تُصيّر الأصل → دالة جديدة → تفكيك وإعادة بناء) فيُنتزع التركيز من الحقل إلى
  // زرّ الإغلاق، والضغطة التالية (Enter/مسافة) تُغلق النافذة. لذلك تُقرأ آخر نسخة
  // منه عبر مرجعٍ، ويُركَّب التأثير مرةً واحدة عند الفتح.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  const dismissibleRef = useRef(dismissible);
  useEffect(() => { dismissibleRef.current = dismissible; }, [dismissible]);
  useEffect(() => {
    const FOCUSABLE = 'a[href],button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex]:not([tabindex="-1"])';
    // التركيز يبقى داخل النافذة (Tab يدور)، ويعود إلى ما كان عليه عند الإغلاق
    const opener = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { if (dismissibleRef.current) onCloseRef.current(); return; }
      if (e.key !== 'Tab' || !ref.current) return;
      const items = Array.from(ref.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !ref.current.contains(active))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // التركيز الأول على أول حقلٍ في الجسم لا على زرّ الإغلاق (الذي يسبق الجسم في
    // ترتيب المستند) — ولا يُنتزع أبداً إن كان التركيز داخل النافذة أصلاً.
    const root = ref.current;
    if (root && !root.contains(document.activeElement)) {
      const body = root.querySelector<HTMLElement>('.modal__body');
      const target = body?.querySelector<HTMLElement>(FOCUSABLE) ?? root.querySelector<HTMLElement>('.modal__foot button') ?? root;
      if (target === root) root.setAttribute('tabindex', '-1');
      target.focus();
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      opener?.focus?.();
    };
  }, []);

  return (
    <div className="modal-bg" onMouseDown={(e) => { if (dismissible && e.target === e.currentTarget) onClose(); }}>
      <div className={`modal${wide ? ' modal--wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref}>
        <header className="modal__head">
          <div className="grow stack gap-1">
            <h2 className="modal__title" id={titleId}>{title}</h2>
            {description && <p className="muted">{description}</p>}
          </div>
          {dismissible && <button type="button" className="icon-btn" onClick={onClose} aria-label="إغلاق النافذة"><X size={18} /></button>}
        </header>
        <div className="modal__body">{children}</div>
        {footer && <footer className="modal__foot">{footer}</footer>}
      </div>
    </div>
  );
}

/** تأكيد الأفعال الخطرة — يسمّي الأثر صراحةً ولا يكتفي بـ«هل أنت متأكد؟» */
export function ConfirmDialog({ title, impact, confirmLabel, onConfirm, onClose, loading, children }: {
  title: string; impact: string; confirmLabel: string;
  onConfirm: () => void; onClose: () => void; loading?: boolean; children?: ReactNode;
}) {
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="danger" onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
          <Button variant="ghost" onClick={onClose}>تراجع</Button>
        </>
      }
    >
      <div className="stack gap-4">
        <Note kind="danger" title="أثر هذا الإجراء">{impact}</Note>
        {children}
      </div>
    </Modal>
  );
}

/* ───────────────────────────── التنبيهات العائمة ───────────────────────────── */
interface Toast { id: number; kind: 'info' | 'success' | 'error'; text: string }
interface ToastApi { push: (kind: Toast['kind'], text: string) => void }
const ToastCtx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const api = useMemo<ToastApi>(() => ({
    push(kind, text) {
      const id = Date.now() + Math.random();
      setItems((s) => [...s, { id, kind, text }]);
      setTimeout(() => setItems((s) => s.filter((t) => t.id !== id)), kind === 'error' ? 8000 : 4500);
    },
  }), []);
  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="toasts" aria-live="polite" aria-atomic="false">
        {items.map((t) => (
          <div key={t.id} className={`toast${t.kind === 'success' ? ' toast--success' : t.kind === 'error' ? ' toast--error' : ''}`} role={t.kind === 'error' ? 'alert' : 'status'}>
            {t.kind === 'success' ? <CheckCircle2 size={17} color="var(--success-700)" /> : t.kind === 'error' ? <ShieldAlert size={17} color="var(--danger-700)" /> : <Info size={17} color="var(--primary-700)" />}
            <span className="grow">{t.text}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast خارج ToastProvider');
  return ctx;
}

/* ───────────────────────────── الخطوات والتبويبات ───────────────────────────── */
export function Steps({ steps, current }: { steps: string[]; current: number }) {
  return (
    <ol className="steps" aria-label="خطوات الإجراء">
      {steps.map((s, i) => (
        <li key={s} className="row gap-2 steps__item">
          <span className={`step${i === current ? ' step--active' : i < current ? ' step--done' : ''}`} aria-current={i === current ? 'step' : undefined} title={s}>
            <span className="step__dot">{i < current ? '✓' : i + 1}</span><span className="step__label">{s}</span>
          </span>
          {i < steps.length - 1 && <span className="step__line" aria-hidden />}
        </li>
      ))}
    </ol>
  );
}

export function Tabs<T extends string>({ tabs, value, onChange }: {
  tabs: Array<{ id: T; label: string; icon?: ReactNode }>; value: T; onChange: (v: T) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.id} type="button" role="tab" className="tab" aria-selected={value === t.id} onClick={() => onChange(t.id)}>
          {t.icon}{t.label}
        </button>
      ))}
    </div>
  );
}

/* ───────────────────────────── ترويسة الصفحة ───────────────────────────── */
export function PageHeader({ title, subtitle, actions, back, crumbs }: {
  title: string; subtitle?: string; actions?: ReactNode; back?: { to: string; label: string };
  /** مسارُ التنقّل (24 أكتوبر 2026): يُبنى وحده من القائمة ورابط العودة؛ الصفحةُ التي تعرف طريقاً أصدق تمرّره، و`false` يطفئه */
  crumbs?: Crumb[] | false;
}) {
  const auto = useAutoCrumbs(title, back);
  const trail = crumbs === false ? null : crumbs ?? auto;
  return (
    <div className="stack gap-3">
      {trail && trail.length >= 2 && <Breadcrumbs items={trail} />}
      {back && !(trail && trail.length >= 2) && (
        <Link to={back.to} className="row gap-1 small no-print" style={{ textDecoration: 'none', color: 'var(--ink-500)' }}>
          <ChevronLeft size={15} style={{ transform: 'scaleX(-1)' }} aria-hidden />{back.label}
        </Link>
      )}
      <div className="page-head">
        <div className="page-head__text">
          <h1 className="page-head__title">{title}</h1>
          {subtitle && <PageSubtitle text={subtitle} />}
        </div>
        {actions && <div className="page-head__actions">{actions}</div>}
      </div>
    </div>
  );
}

/**
 * شرحُ الصفحة سطرٌ واحد (22 سبتمبر 2026 — «كلام طويل ماله داعي»): الجملةُ الأولى تُعرض، والباقي
 * خلف «المزيد» يفتحه من أراد. القاعدةُ عامّةٌ على كل الشاشات فلا يُعاد كتابةُ ستّين شرحاً.
 */
function PageSubtitle({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const cut = (() => {
    if (text.length <= 110) return -1;
    const m = /[.—:؛]\s/.exec(text.slice(40));
    const at = m ? 40 + m.index + 1 : text.lastIndexOf(' ', 100);
    return at > 30 && at < text.length - 20 ? at : -1;
  })();
  if (cut < 0 || open) return <p className="page-head__sub">{text}{cut >= 0 && <> <button type="button" className="link-btn" onClick={() => setOpen(false)}>أقلّ</button></>}</p>;
  return <p className="page-head__sub">{text.slice(0, cut).trim()} <button type="button" className="link-btn" onClick={() => setOpen(true)}>المزيد…</button></p>;
}

/** قائمة وصف — لعرض تفاصيل كيان بلا جدول */
/** نص LTR داخل سياق عربي — يمنع قفز الأرقام والعلامات إلى الطرف الخطأ */
/**
 * الرقمُ المرجعي (RF-… / RC-… / AC-…) أينما ظهر: قرصٌ يُنسخ بضغطةٍ واحدة — كما في
 * «أبنائي» (طلب صاحب المنصة 20 سبتمبر 2026). فالرقمُ يُملى لمركزٍ أو يُلصق في طلبٍ،
 * ونسخُه بالتحديد على الهاتف عناءٌ. والنسخُ يُعلَن بنبضة toast لا بصمت.
 */
export function RefCode({ code, label = 'الرقم المرجعي' }: { code: string; label?: string }) {
  const toast = useToast();
  return (
    <button type="button" className="refcode" title={`انسخ ${label}`} aria-label={`انسخ ${label} ${code}`}
      onClick={() => { void navigator.clipboard?.writeText(code); toast.push('success', `نُسخ ${label} ${code}.`); }}>
      <bdi dir="ltr" className="latin">{code}</bdi><Copy size={13} aria-hidden />
    </button>
  );
}

export function Ltr({ children }: { children: ReactNode }) {
  return <bdi dir="ltr" className="latin">{children}</bdi>;
}

/**
 * مبلغٌ بعملته — لا رقمَ مالياً يُعرض عارياً في المنصة.
 * الأرقام لاتينية في كل الشاشات (قرار مثبَّت في lib/format)، ويُعزل الرقم بـ`Ltr`
 * فلا تهاجر الفاصلة العشرية إلى طرف السطر في سياق عربي.
 */
export function Money({ amount, currency, signed }: { amount: number | null | undefined; currency: string; signed?: boolean }) {
  if (amount === null || amount === undefined) return <>—</>;
  const sign = signed && amount > 0 ? '+' : '';
  // المبلغ وعملته وحدةٌ واحدة لا تُكسر على سطرين — رقمٌ مقطوعٌ عن عملته يُقرأ خطأً
  return <span style={{ whiteSpace: 'nowrap' }}><Ltr>{sign}{amount.toFixed(2)}</Ltr> {currencyLabel[currency] ?? currency}</span>;
}

export function DescList({ items }: { items: Array<{ k: string; v: ReactNode }> }) {
  return (
    <dl className="dl">
      {items.map((it) => (
        <div className="dl__item" key={it.k}>
          <dt className="dl__k">{it.k}</dt>
          <dd className="dl__v">{it.v}</dd>
        </div>
      ))}
    </dl>
  );
}
